import { ChangeRows } from '@/components/ui/change-rows';
import { StatusChip, type StatusChipVariant } from '@/components/ui/status-chip';
import { pluralize } from '@/utils/string';
import type { ChangeGroup, ChangeKind } from './settings-changes';

const KIND_CHIPS: Record<ChangeKind, { label: string; variant: StatusChipVariant }> = {
  changed: { label: 'Changed', variant: 'info' },
  added: { label: 'New', variant: 'success' },
  removed: { label: 'Removed', variant: 'destructive' },
};

export const SettingsChangesList = ({ groups }: { groups: ChangeGroup[] }) => {
  // Downtime groups are keyed `downtime:…`; every other added or removed group is a schedule.
  const countOf = (kind: ChangeKind, isDowntime: boolean) => groups.filter((group) => group.kind === kind && group.key.startsWith('downtime:') === isDowntime).length;
  const fieldChanges = groups.filter((group) => group.kind === 'changed').reduce((total, group) => total + group.rows.length, 0);
  const parts = [
    fieldChanges ? pluralize(fieldChanges, 'field change') : '',
    countOf('added', false) ? pluralize(countOf('added', false), 'new schedule') : '',
    countOf('removed', false) ? pluralize(countOf('removed', false), 'removed schedule') : '',
    countOf('added', true) ? pluralize(countOf('added', true), 'new downtime rule') : '',
    countOf('removed', true) ? pluralize(countOf('removed', true), 'removed downtime rule') : '',
  ].filter(Boolean);
  const summary = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts[0];
  return (
    <div className="flex flex-col gap-3 text-sm text-foreground">
      <p className="text-muted-foreground">{summary}</p>
      {groups.map((group) => (
        <section className="overflow-hidden rounded-md border border-border" key={group.key}>
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
            <span className="flex flex-col">
              <span className="font-medium">{group.title}</span>
              {group.subtitle ? <span className="text-xs text-muted-foreground">{group.subtitle}</span> : null}
            </span>
            <StatusChip className="px-2 py-0.5 text-xs" variant={KIND_CHIPS[group.kind].variant}>
              {KIND_CHIPS[group.kind].label}
            </StatusChip>
          </header>
          <ChangeRows rows={group.rows} />
        </section>
      ))}
    </div>
  );
};
SettingsChangesList.displayName = 'SettingsChangesList';
